import React, { useCallback, useRef, useState, useEffect } from 'react';
import Moveable from 'react-moveable';
import { Frame, setAlias } from 'scenejs';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';

export const OverLayRender = ({ element, uuid, methods }) => {

    setAlias('tx', ['transform', 'translateX']);
    setAlias('ty', ['transform', 'translateY']);
    setAlias('tr', ['transform', 'rotate']);
    setAlias('sx', ['transform', 'scaleX']);
    setAlias('sy', ['transform', 'scaleY']);

    const contextSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
    const [DocumentStyles] = useDocumentContext(contextSelector);

    const flowSelector = useCallback(store => store[uuid], [uuid]);
    const [flow] = useDocumentContext(flowSelector, `PageNumberSettings`);
    const overLayDetails = flow['overlayText'];
    const overLayDetailsRef = useRef(overLayDetails);
    const isInteractingRef = useRef(false);
    const elementRef = useRef();
    const moveableRef = useRef();
    const containerRef = useRef();
    const contentRef = useRef();
    const frameRef = useRef(null);
    const scalingRef = useRef(1);
    const originalDimensionsRef = useRef({ width: 0, height: 0, fontSize: 0 });
    const lastDirectionRef = useRef(null);
    const contents = element?.contents.contents;
    const style = element.contents.style;
    const [isSelected, setIsSelected] = useState(false);
    const [elementStyle, setElementStyle] = useState({
        ...contents[0].style,
        color: overLayDetails.contents.style.color || '#000000',
        fontSize: overLayDetails.contents.style.fontSize || contents[0].style?.fontSize,
        fontFamily: overLayDetails.contents.style.fontFamily || contents[0].style?.fontFamily,
    });

    useEffect(() => {
        overLayDetailsRef.current = overLayDetails;
    }, [overLayDetails]);

    const getFrame = useCallback(() => {
        if (!frameRef.current) {
            frameRef.current = new Frame({
                transform: {
                    translateX: `${style.x || 0}px`,
                    translateY: `${style.y || 0}px`,
                    rotate: `${style.rotate || 0}deg`,
                },
                width: `${contents[0].style.width}px`,
                height: `${contents[0].style.height}px`,
            });
        }
        return frameRef.current;
    }, []);

    useEffect(() => {
        if (frameRef.current && !isInteractingRef.current) {
            frameRef.current.set('tx', `${style.x || 0}px`);
            frameRef.current.set('ty', `${style.y || 0}px`);
            frameRef.current.set('tr', `${style.rotate || 0}deg`);
            frameRef.current.set('width', `${contents[0].style.width}px`);
            frameRef.current.set('height', `${contents[0].style.height}px`);
            applyFrame();
        }
    }, [style.x, style.y, style.rotate, contents[0].style.width, contents[0].style.height]);

    useEffect(() => {
        setElementStyle({
            ...contents[0].style,
            color: overLayDetails.contents.style.color || '#000000',
            fontSize: overLayDetails.contents.style.fontSize || contents[0].style?.fontSize,
            fontFamily: overLayDetails.contents.style.fontFamily || contents[0].style?.fontFamily,
        });
    }, [overLayDetails]);

    const applyFrame = useCallback(() => {
        if (elementRef.current) {
            elementRef.current.style.cssText += getFrame().toCSS();
            moveableRef.current && moveableRef.current.updateRect();
        }
    }, [getFrame]);

    const onEnd = useCallback(() => {
        isInteractingRef.current = false;
        const label = document.querySelectorAll('.moveable-label')[0];
        if (label) {
            label.style.display = 'none';
        }
        if (
            moveableRef.current &&
            moveableRef.current.refTargets &&
            moveableRef.current.refTargets.length > 0
        ) {
            moveableRef.current.refTargets[0].classList.remove('is-dragging');
        }
    }, []);

    const containerStyle = {
        ...style,
        position: 'absolute',
        zIndex: 3,
        transform: `translateX(${style.x || 0}px) translateY(${style.y || 0}px) rotate(${style.rotate || 0}deg)`,
        transformOrigin: '0 0',
        width: `${overLayDetails.contents.style.width}px`,
        height: `${overLayDetails.contents.style.height}px`,
        background: style.backgroundColor,
        overflow: 'hidden',
        x: undefined,
        y: undefined,
        color: overLayDetails.contents.style.color || '#000000',
        fontFamily: overLayDetails?.contents.style.fontFamily || DocumentStyles.fontFamily,
        fontSize: overLayDetails?.contents.style.fontSize || DocumentStyles.fontSize,
    };

    const handleDragStart = ({ set }) => {
        isInteractingRef.current = true;
        const frame = getFrame();
        set([
            parseFloat(frame.get('tx')) || 0,
            parseFloat(frame.get('ty')) || 0,
        ]);
    };

    const handleDrag = ({ beforeTranslate }) => {
        const frame = getFrame();
        frame.set('tx', `${beforeTranslate[0]}px`);
        frame.set('ty', `${beforeTranslate[1]}px`);
        applyFrame();
    };

    const handleDragEnd = ({ lastEvent }) => {
        onEnd();
        if (!lastEvent) return;
        const frame = getFrame();
        const newX = parseFloat(frame.get('tx'));
        const newY = parseFloat(frame.get('ty'));
        const latest = overLayDetailsRef.current;

        const updatedContents = {
            ...latest.contents,
            style: {
                ...latest.contents.style,
                x: newX,
                y: newY,
                width: latest.contents.style.width,
                height: latest.contents.style.height,
                rotate: latest.contents.style.rotate,
            }
        };
        methods.updateOverlayText({
            uuid,
            block: { contents: updatedContents, pageNumberType: 1 },
        });
    };

    const handleRotateStart = ({ set, dragStart }) => {
        isInteractingRef.current = true;
        const frame = getFrame();
        set(parseFloat(frame.get('tr')) || 0);
        if (dragStart) {
            dragStart.set([
                parseFloat(frame.get('tx')) || 0,
                parseFloat(frame.get('ty')) || 0,
            ]);
        }
    };

    const handleRotate = ({ beforeDelta, drag, clientX, clientY }) => {
        const frame = getFrame();
        const deg = parseFloat(frame.get('tr')) + beforeDelta;
        frame.set('tr', `${deg}deg`);
        if (drag) {
            frame.set('tx', `${drag.beforeTranslate[0]}px`);
            frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        applyFrame();
        setLabel(clientX, clientY, `R: ${deg.toFixed(1)}`);
    };

    const handleRotateEnd = ({ lastEvent }) => {
        onEnd();
        if (!lastEvent) return;
        const frame = getFrame();
        const newX = parseFloat(frame.get('tx'));
        const newY = parseFloat(frame.get('ty'));
        const newRotate = parseFloat(frame.get('tr'));
        applyFrame();
        const latest = overLayDetailsRef.current;

        const updatedContents = {
            ...latest.contents,
            style: {
                ...latest.contents.style,
                x: newX,
                y: newY,
                rotate: newRotate,
                width: latest.contents.style.width,
                height: latest.contents.style.height,
            }
        };
        methods.updateOverlayText({
            uuid,
            block: { contents: updatedContents, pageNumberType: 1 },
        });
    };

    const handleResizeStart = ({ target, setOrigin, dragStart }) => {
        isInteractingRef.current = true;
        const frame = getFrame();

        const currentFontSize =
            parseFloat(overLayDetailsRef.current.contents.style.fontSize) ||
            parseFloat(overLayDetailsRef.current.contents.contents[0].style.fontSize) ||
            16;

        originalDimensionsRef.current = {
            width: parseFloat(frame.get('width')) || target.offsetWidth,
            height: parseFloat(frame.get('height')) || target.offsetHeight,
            fontSize: currentFontSize,
        };
        scalingRef.current = 1;
        lastDirectionRef.current = null;

        if (contentRef.current) {
            contentRef.current.style.whiteSpace = '';
            contentRef.current.style.wordBreak = '';
        }
        if (elementRef.current) {
            elementRef.current.style.height = `${originalDimensionsRef.current.height}px`;
        }

        setOrigin(['%', '%']);
        if (dragStart) {
            dragStart.set([
                parseFloat(frame.get('tx')) || 0,
                parseFloat(frame.get('ty')) || 0,
            ]);
        }
    };

    const handleResize = ({ target, width, height, drag, direction, clientX, clientY }) => {
        const frame = getFrame();
        if (direction[0] !== 0) {
            frame.set('tx', `${drag.beforeTranslate[0]}px`);
        }
        if (direction[1] !== 0) {
            frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        const originalWidth = originalDimensionsRef.current.width;
        const originalHeight = originalDimensionsRef.current.height;
        const originalFontSize = originalDimensionsRef.current.fontSize;
        if (direction[1] !== 0 && direction[0] !== 0) {
            lastDirectionRef.current = 'corner';
            let scaling;
            if (
                (direction[0] === 1 && direction[1] === 1) ||
                (direction[0] === 1 && direction[1] === -1)
            ) {
                scaling = height / originalHeight;
            } else {
                scaling = width / originalWidth;
            }
            const newWidth = scaling * originalWidth;
            const newHeight = scaling * originalHeight;
            const newFontSize = originalFontSize * scaling;
            frame.set('width', `${newWidth.toFixed(3)}px`);
            frame.set('height', `${newHeight.toFixed(3)}px`);
            scalingRef.current = scaling;
            if (contentRef.current) {
                contentRef.current.style.fontSize = `${newFontSize.toFixed(3)}px`;
            }
            if (elementRef.current) {
                elementRef.current.style.height = `${newHeight.toFixed(3)}px`;
            }
            if (clientX && clientY) {
                const calculatedWidth = (newWidth * (25.4 / 96)).toFixed(1);
                const calculatedHeight = (newHeight * (25.4 / 96)).toFixed(1);
                setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`);
            }
        } else {
            lastDirectionRef.current = 'edge';
            scalingRef.current = 1;
            if (direction[0] !== 0) {
                frame.set('width', `${width.toFixed(3)}px`);
                if (elementRef.current) {
                    elementRef.current.style.width = `${width.toFixed(3)}px`;
                    elementRef.current.style.height = 'auto';
                }
                const naturalHeight = elementRef.current
                    ? elementRef.current.scrollHeight
                    : originalHeight;
                frame.set('height', `${naturalHeight}px`);
            } else {
                frame.set('height', `${height.toFixed(3)}px`);
            }
            if (clientX && clientY) {
                const currentWidth  = parseFloat(frame.get('width'));
                const currentHeight = parseFloat(frame.get('height'));
                const calculatedWidth  = (currentWidth  * (25.4 / 96)).toFixed(1);
                const calculatedHeight = (currentHeight * (25.4 / 96)).toFixed(1);
                setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`);
            }
        }

        applyFrame();
    };

    const handleResizeEnd = ({ lastEvent }) => {
        onEnd();
        if (!lastEvent) return;
        const frame = getFrame();
        const latest = overLayDetailsRef.current;

        const newWidth  = parseFloat(frame.get('width'));
        const newHeight = parseFloat(frame.get('height'));
        const newX = parseFloat(frame.get('tx'));
        const newY = parseFloat(frame.get('ty'));

        const originalFontSize = originalDimensionsRef.current.fontSize;
        const isEdgeResize = lastDirectionRef.current === 'edge';
        const newFontSize = isEdgeResize
            ? originalFontSize
            : originalFontSize * scalingRef.current;

        if (isEdgeResize && elementRef.current) {
            elementRef.current.style.height = `${newHeight}px`;
        }

        const updatedContents = {
            ...latest.contents,
            style: {
                ...latest.contents.style,
                x: newX,
                y: newY,
                width: newWidth,
                height: newHeight,
                fontSize: newFontSize,
                rotate: latest.contents.style.rotate,
            },
            contents: latest.contents.contents.map((item, index) => {
                if (index === 0) {
                    return {
                        ...item,
                        style: {
                            ...item.style,
                            fontSize: newFontSize,
                            width: newWidth,
                            height: newHeight,
                        }
                    };
                }
                return item;
            })
        };
        methods.updateOverlayText({
            uuid,
            block: { contents: updatedContents, pageNumberType: 1 },
        });
        scalingRef.current = 1;
        lastDirectionRef.current = null;
    };

    const setLabel = (clientX, clientY, text) => {
        const label = document.querySelectorAll('.moveable-label')[0];
        if (!label) return;
        label.style.cssText = `
            display: block; transform: translate(${clientX}px, ${clientY - 10}px) translate(-100%, -100%) translateZ(-100px);`;
        label.innerHTML = text;
        if (
            moveableRef.current &&
            moveableRef.current.refTargets &&
            moveableRef.current.refTargets.length > 0
        ) {
            moveableRef.current.refTargets[0].classList.add('is-dragging');
        }
    };


    return (
        <>
            <div
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setIsSelected(false);
                    }
                }}
                ref={containerRef}
                tabIndex={-1}
                style={{ outline: 'none' }}
            >
                <span
                    ref={elementRef}
                    style={containerStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsSelected(true);
                        containerRef.current.focus();
                    }}
                >
                    <span ref={contentRef} style={elementStyle}>
                        {contents[0].content}
                    </span>
                </span>

                <Moveable
                    className={isSelected ? '' : 'hide-moveable'}
                    ref={moveableRef}
                    rotatable={true}
                    resizable={true}
                    draggable={true}
                    origin={false}
                    edgeDraggable={true}
                    originDraggable={false}
                    keepRatio={false}
                    rotationPosition="bottom"
                    renderDirections={['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w']}
                    target={isSelected ? elementRef.current : null}
                    pinchable={true}
                    dragArea={true}
                    throttleDrag={0}
                    throttleResize={0}
                    throttleRotate={0}
                    onDragStart={handleDragStart}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    onRotateStart={handleRotateStart}
                    onRotate={handleRotate}
                    onRotateEnd={handleRotateEnd}
                    onResizeStart={handleResizeStart}
                    onResize={handleResize}
                    onResizeEnd={handleResizeEnd}
                />
            </div>
        </>
    );
};